import { allRowsSplit, type GridTuning } from "@/character/draft";
import {
  SHEET_COLUMNS,
  fitSheetToMaxWidth,
  normalizeGridSpec,
  rowSplitOffsetX,
  rowSplitOffsetY,
  sheetVerticalLineXs,
  sourceFrameRect,
  sheetToDisplayX,
  sheetToDisplayY,
  type CharacterGridSpec,
  type SheetLayout,
} from "@/character/grid";

const DEFAULT_FPS = 8;

export function drawGridSheet(
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  layout: SheetLayout,
  maxWidth: number,
  tuning?: GridTuning,
  activeRow?: number | null,
): void {
  const { displayWidth, displayHeight } = fitSheetToMaxWidth(
    layout.sheetWidth,
    layout.sheetHeight,
    maxWidth,
  );

  canvas.width = displayWidth;
  canvas.height = displayHeight;
  canvas.style.width = "100%";
  canvas.style.height = "auto";
  canvas.style.aspectRatio = `${displayWidth} / ${displayHeight}`;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const grid = normalizeGridSpec(layout.grid);
  const { sheetWidth, sheetHeight } = layout;
  const ax = grid.alignX;
  const ay = grid.alignY;

  ctx.clearRect(0, 0, displayWidth, displayHeight);
  ctx.drawImage(image, 0, 0, displayWidth, displayHeight);

  const perRowGrid = tuning ? allRowsSplit(tuning) : false;
  const fh = grid.frameHeight;

  const rowBand = (row: number) => {
    const extraY = rowSplitOffsetY(tuning, row);
    const topSheetY = ay + row * fh + extraY;
    const bottomSheetY = topSheetY + fh;
    const y0 = sheetToDisplayY(topSheetY, sheetHeight, displayHeight);
    const y1 = sheetToDisplayY(bottomSheetY, sheetHeight, displayHeight);
    return { topSheetY, bottomSheetY, y0, y1 };
  };

  const strokeHLine = (sheetY: number, color: string, width = 1) => {
    const y = sheetToDisplayY(sheetY, sheetHeight, displayHeight);
    const py = Math.round(y) + 0.5;
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(0, py);
    ctx.lineTo(displayWidth, py);
    ctx.stroke();
  };

  const strokeVLineSegment = (sheetX: number, topSheetY: number, bottomSheetY: number, color: string) => {
    const x = sheetToDisplayX(sheetX, sheetWidth, displayWidth);
    const px = Math.round(x) + 0.5;
    const y0 = sheetToDisplayY(topSheetY, sheetHeight, displayHeight);
    const y1 = sheetToDisplayY(bottomSheetY, sheetHeight, displayHeight);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(px, y0);
    ctx.lineTo(px, y1);
    ctx.stroke();
  };

  if (perRowGrid) {
    if (activeRow != null) {
      for (let row = 0; row < grid.rows; row += 1) {
        if (row === activeRow) continue;
        const { y0, y1 } = rowBand(row);
        ctx.fillStyle = "rgba(5, 8, 18, 0.58)";
        ctx.fillRect(0, y0, displayWidth, y1 - y0);
      }
    }

    for (let row = 0; row < grid.rows; row += 1) {
      const { topSheetY, bottomSheetY, y0, y1 } = rowBand(row);
      const active = activeRow === row;
      const color = active
        ? "rgba(255, 220, 120, 0.98)"
        : "rgba(255, 200, 90, 0.75)";
      const dim = active ? 0.14 : 0.06;
      if (grid.sheetColumns === SHEET_COLUMNS) {
        const labelRight = sheetToDisplayX(ax + grid.regionX, sheetWidth, displayWidth);
        ctx.fillStyle = `rgba(110, 200, 255, ${dim})`;
        ctx.fillRect(0, y0, labelRight, y1 - y0);
      }
      strokeHLine(topSheetY, color, active ? 1.5 : 1);
      strokeHLine(bottomSheetY, color, active ? 1.5 : 1);
      const extraX = rowSplitOffsetX(tuning, row);
      for (const sheetX of sheetVerticalLineXs(grid)) {
        strokeVLineSegment(sheetX + extraX, topSheetY, bottomSheetY, color);
      }
    }
    return;
  }

  if (grid.sheetColumns === SHEET_COLUMNS) {
    const labelRight = sheetToDisplayX(ax + grid.regionX, sheetWidth, displayWidth);
    ctx.fillStyle = "rgba(110, 200, 255, 0.08)";
    ctx.fillRect(0, 0, labelRight, displayHeight);
  }

  ctx.strokeStyle = "rgba(110, 200, 255, 0.85)";
  ctx.lineWidth = 1;

  for (const sheetX of sheetVerticalLineXs(grid)) {
    const x = sheetToDisplayX(sheetX, sheetWidth, displayWidth);
    const px = Math.round(x) + 0.5;
    ctx.beginPath();
    ctx.moveTo(px, 0);
    ctx.lineTo(px, displayHeight);
    ctx.stroke();
  }

  for (let row = 0; row < grid.rows; row += 1) {
    const extraY = rowSplitOffsetY(tuning, row);
    const topSheetY = ay + row * fh + extraY;
    const bottomSheetY = topSheetY + fh;
    for (const sheetY of [topSheetY, bottomSheetY]) {
      strokeHLine(sheetY, "rgba(110, 200, 255, 0.85)");
    }
  }
}

export function drawRowStrip(
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  layout: SheetLayout,
  row: number,
  tuning?: GridTuning,
  cellSize = 56,
): void {
  const { grid } = layout;
  canvas.width = cellSize * grid.columns;
  canvas.height = cellSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.fillStyle = "#0a0e14";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const frame = sourceFrameRect(layout, 0, row, tuning);
  const scale = cellSize / frame.sh;

  for (let col = 0; col < grid.columns; col += 1) {
    const rect = sourceFrameRect(layout, col, row, tuning);
    const dx = col * cellSize;
    ctx.drawImage(
      image,
      rect.sx,
      rect.sy,
      rect.sw,
      rect.sh,
      dx,
      0,
      rect.sw * scale,
      rect.sh * scale,
    );
    ctx.strokeStyle = "#2a3548";
    ctx.strokeRect(dx + 0.5, 0.5, cellSize - 1, cellSize - 1);
  }
}

export function sheetLayoutFromGrid(
  image: Pick<HTMLImageElement, "naturalWidth" | "naturalHeight">,
  grid: CharacterGridSpec,
): SheetLayout {
  return {
    sheetWidth: image.naturalWidth,
    sheetHeight: image.naturalHeight,
    grid: normalizeGridSpec(grid),
  };
}

/** Draw row loop scaled to a target on-screen height (same rule as Phaser home room). */
export function startRowLoopAtHeight(
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  layout: SheetLayout,
  row: number,
  displayHeight: number,
  fps = DEFAULT_FPS,
  mode: "loop" | "once" = "loop",
  onComplete?: () => void,
  tuning?: GridTuning,
): () => void {
  const sample = sourceFrameRect(layout, 0, row, tuning);
  const scale = displayHeight / sample.sh;
  const displayWidth = Math.max(1, Math.round(sample.sw * scale));
  canvas.width = displayWidth;
  canvas.height = displayHeight;
  canvas.style.width = `${displayWidth}px`;
  canvas.style.height = `${displayHeight}px`;

  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};

  const { grid } = layout;
  let column = 0;
  let lastTime = 0;
  let rafId = 0;
  const frameMs = 1000 / fps;

  const drawFrame = (col: number) => {
    const rect = sourceFrameRect(layout, col, row, tuning);
    ctx.clearRect(0, 0, displayWidth, displayHeight);

    const frameScale = displayHeight / rect.sh;
    const dw = rect.sw * frameScale;
    const dh = rect.sh * frameScale;
    const dx = (displayWidth - dw) / 2;
    const dy = displayHeight - dh;

    ctx.drawImage(image, rect.sx, rect.sy, rect.sw, rect.sh, dx, dy, dw, dh);
  };

  let finished = false;

  const tick = (time: number) => {
    if (finished) return;
    if (time - lastTime >= frameMs) {
      lastTime = time;
      drawFrame(column);
      if (mode === "once") {
        if (column >= grid.columns - 1) {
          finished = true;
          onComplete?.();
          return;
        }
        column += 1;
      } else {
        column = (column + 1) % grid.columns;
      }
    }
    rafId = requestAnimationFrame(tick);
  };

  drawFrame(0);
  rafId = requestAnimationFrame(tick);

  return () => {
    cancelAnimationFrame(rafId);
  };
}

export function startRowLoop(
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  layout: SheetLayout,
  row: number,
  fps = DEFAULT_FPS,
  mode: "loop" | "once" = "loop",
  onComplete?: () => void,
  tuning?: GridTuning,
): () => void {
  const size =
    canvas.width > 0 && canvas.height > 0 && canvas.width === canvas.height
      ? canvas.width
      : 96;
  return startRowLoopAtHeight(canvas, image, layout, row, size, fps, mode, onComplete, tuning);
}
